import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { PENDING_SUBSCRIPTION_STORAGE_KEY } from '../subscriptions/subscription.model';
import {
  AddToCartInput,
  CART_EXPIRY_DAYS,
  CART_SESSION_COOKIE,
  CART_STORAGE_KEY,
  CartItem,
  FREE_SHIPPING_THRESHOLD,
  StoredCartData,
} from './cart.model';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private readonly _items = signal<CartItem[]>([]);
  private readonly _pendingCouponCode = signal('');
  private readonly _appliedCoupon = signal<{ code: string; discountAmount: number } | null>(null);
  private readonly _isDrawerOpen = signal(false);
  private readonly _isNavigatingToCheckout = signal(false);

  readonly items = this._items.asReadonly();
  readonly pendingCouponCode = this._pendingCouponCode.asReadonly();
  readonly appliedCoupon = this._appliedCoupon.asReadonly();
  readonly isDrawerOpen = this._isDrawerOpen.asReadonly();
  readonly isNavigatingToCheckout = this._isNavigatingToCheckout.asReadonly();
  readonly itemCount = computed(() => this._items().length);
  readonly subtotal = computed(() =>
    Math.round(this._items().reduce((sum, item) => sum + item.unitPrice * item.quantity, 0) * 100) / 100,
  );
  readonly couponDiscount = computed(() => this._appliedCoupon()?.discountAmount ?? 0);
  readonly grandTotal = computed(() =>
    Math.max(0, Math.round((this.subtotal() - this.couponDiscount()) * 100) / 100),
  );
  readonly freeShippingProgress = computed(() =>
    Math.min(100, Math.round((this.subtotal() / FREE_SHIPPING_THRESHOLD) * 100)),
  );
  readonly amountUntilFreeShipping = computed(() =>
    Math.max(0, Math.round((FREE_SHIPPING_THRESHOLD - this.subtotal()) * 100) / 100),
  );
  readonly hasFreeShipping = computed(() => this.subtotal() >= FREE_SHIPPING_THRESHOLD);

  constructor() {
    this.loadCart();
  }

  lineKey(catalogId: number, variantId: number | null): string {
    return `${catalogId}:${variantId ?? 0}`;
  }

  lineTotal(item: CartItem): number {
    return Math.round(item.unitPrice * item.quantity * 100) / 100;
  }

  sessionId(): string | null {
    return this.getCookie(CART_SESSION_COOKIE);
  }

  addItem(input: AddToCartInput): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const key = this.lineKey(input.catalogId, input.variantId);
    const items = [...this._items()];
    const existing = items.find((item) => this.lineKey(item.catalogId, item.variantId) === key);

    if (existing) {
      existing.quantity = Math.min(99, existing.quantity + input.quantity);
      existing.unitPrice = input.unitPrice;
      existing.imageUrl = input.imageUrl;
      existing.productName = input.productName;
      existing.variantName = input.variantName;
      existing.catalogSlug = input.catalogSlug;
    } else {
      items.push({
        catalogId: input.catalogId,
        catalogSlug: input.catalogSlug,
        variantId: input.variantId,
        variantName: input.variantName,
        productName: input.productName,
        imageUrl: input.imageUrl,
        unitPrice: input.unitPrice,
        quantity: Math.min(99, Math.max(1, input.quantity)),
      });
    }

    this._items.set(items);
    this.persist();
  }

  addItems(inputs: AddToCartInput[]): void {
    for (const input of inputs) {
      this.addItem(input);
    }
  }

  updateQuantity(catalogId: number, variantId: number | null, quantity: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const key = this.lineKey(catalogId, variantId);
    const nextQty = Math.min(99, Math.max(1, quantity));
    const items = this._items().map((item) =>
      this.lineKey(item.catalogId, item.variantId) === key ? { ...item, quantity: nextQty } : item,
    );
    this._items.set(items);
    this.persist();
  }

  incrementItem(catalogId: number, variantId: number | null): void {
    const key = this.lineKey(catalogId, variantId);
    const item = this._items().find((i) => this.lineKey(i.catalogId, i.variantId) === key);
    if (item) {
      this.updateQuantity(catalogId, variantId, item.quantity + 1);
    }
  }

  decrementItem(catalogId: number, variantId: number | null): void {
    const key = this.lineKey(catalogId, variantId);
    const item = this._items().find((i) => this.lineKey(i.catalogId, i.variantId) === key);
    if (item) {
      this.updateQuantity(catalogId, variantId, item.quantity - 1);
    }
  }

  removeItem(catalogId: number, variantId: number | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const key = this.lineKey(catalogId, variantId);
    const items = this._items().filter((item) => this.lineKey(item.catalogId, item.variantId) !== key);
    this._items.set(items);

    // User abandoned the bag: drop coupons + pending subscription checkout session.
    // Do not do this in clearCart() — that path is also used mid-subscription setup/payment.
    if (items.length === 0) {
      this._pendingCouponCode.set('');
      this._appliedCoupon.set(null);
      this._isDrawerOpen.set(false);
      this.clearStoredCart();
      this.clearPendingSubscriptionCheckout();
      return;
    }

    this.persist();
  }

  clearCart(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this._items.set([]);
    this._pendingCouponCode.set('');
    this._appliedCoupon.set(null);
    this._isDrawerOpen.set(false);
    this.clearStoredCart();
  }

  setPendingCouponCode(code: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this._pendingCouponCode.set(code.trim());
    this.persist();
  }

  setAppliedCoupon(coupon: { code: string; discountAmount: number } | null): void {
    this._appliedCoupon.set(coupon);
    this.setPendingCouponCode(coupon?.code ?? '');
  }

  openDrawer(): void {
    this._isDrawerOpen.set(true);
  }

  closeDrawer(): void {
    this._isDrawerOpen.set(false);
  }

  goToCheckout(): void {
    this._isNavigatingToCheckout.set(true);
    this._isDrawerOpen.set(false);

    if (this.authService.isAuthenticated()) {
      void this.router.navigate(['/checkout']);
      return;
    }

    void this.router.navigate(['/login'], { queryParams: { returnUrl: '/checkout' } });
  }

  clearCheckoutNavigation(): void {
    this._isNavigatingToCheckout.set(false);
  }

  private loadCart(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const sessionId = this.ensureSessionId();
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      this._items.set([]);
      this._pendingCouponCode.set('');
      return;
    }

    try {
      const data = JSON.parse(raw) as StoredCartData;
      if (data.expiresAt < Date.now() || data.sessionId !== sessionId) {
        this.clearStoredCart();
        this._items.set([]);
        this._pendingCouponCode.set('');
        return;
      }
      this._items.set(data.items ?? []);
      this._pendingCouponCode.set(data.pendingCouponCode ?? '');
    } catch {
      this.clearStoredCart();
      this._items.set([]);
      this._pendingCouponCode.set('');
    }
  }

  private persist(): void {
    const sessionId = this.ensureSessionId();
    const expiresAt = Date.now() + CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const data: StoredCartData = {
      sessionId,
      expiresAt,
      items: this._items(),
      pendingCouponCode: this._pendingCouponCode(),
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(data));
    this.setCookie(CART_SESSION_COOKIE, sessionId, CART_EXPIRY_DAYS);
  }

  private ensureSessionId(): string {
    const existing = this.getCookie(CART_SESSION_COOKIE);
    if (existing) {
      this.setCookie(CART_SESSION_COOKIE, existing, CART_EXPIRY_DAYS);
      return existing;
    }

    const sessionId = this.createSessionId();
    this.setCookie(CART_SESSION_COOKIE, sessionId, CART_EXPIRY_DAYS);
    return sessionId;
  }

  /** Secure contexts (HTTPS) can use crypto.randomUUID; HTTP deployments need a fallback. */
  private createSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private clearStoredCart(): void {
    localStorage.removeItem(CART_STORAGE_KEY);
  }

  private clearPendingSubscriptionCheckout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    sessionStorage.removeItem(PENDING_SUBSCRIPTION_STORAGE_KEY);
  }

  private setCookie(name: string, value: string, days: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  }

  private getCookie(name: string): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const nameEq = `${name}=`;
    const parts = document.cookie.split(';');
    for (const part of parts) {
      let c = part;
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(nameEq) === 0) {
        return decodeURIComponent(c.substring(nameEq.length));
      }
    }
    return null;
  }
}
